import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDownIcon, 
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
  TrophyIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const FAQ = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', 'Participation', 'Rewards', 'Technical', 'General'];

  const faqs = [
    {
      id: 1,
      category: 'Participation',
      question: "Why is there a commitment fee for certain competitions?",
      answer: "A commitment fee ensures serious participation and enables us to allocate expert reviewers for every submission. This maintains evaluation quality and platform integrity."
    },
    {
      id: 2,
      category: 'Participation',
      question: "Who is eligible to participate?",
      answer: "Students, graduates, and working professionals are welcome. Each competition clearly mentions eligibility criteria in its overview section."
    },
    {
      id: 3,
      category: 'Rewards',
      question: "How are rewards distributed?",
      answer: "After the evaluation phase concludes, winners are notified via email and dashboard. Monetary rewards are processed securely, while certificates and recognitions are issued digitally."
    },
    {
      id: 4,
      category: 'Rewards',
      question: "Will I receive a certificate if I don’t win?",
      answer: "Yes. All qualifying submissions receive a Certificate of Participation as recognition of effort and skill demonstration."
    },
    {
      id: 5,
      category: 'General',
      question: "How transparent is the evaluation process?",
      answer: "We follow structured scoring rubrics and expert validation layers to ensure fairness, consistency, and bias-free assessments."
    },
    {
      id: 6,
      category: 'General',
      question: "What is your refund policy?",
      answer: "Commitment fees are non-refundable once the competition begins. However, in rare cases of technical failure from our end, refunds are processed accordingly."
    },
    {
      id: 7,
      category: 'General',
      question: "Is my data secure on this platform?",
      answer: "Yes. We follow strict data protection practices. Your information is encrypted and never shared with third parties without consent."
    },
    {
      id: 8,
      category: 'Technical',
      question: "The submission button is not working.",
      answer: "Please ensure your file is under 20MB and in the required format (PDF/ZIP). Try refreshing your browser or switching networks if the issue persists."
    },
    {
      id: 9,
      category: 'Technical',
      question: "I didn’t receive my verification email.",
      answer: "Check your spam or promotions folder. If not found, wait a few minutes and use the resend option. Ensure your email address was entered correctly."
    }
  ];

  const filteredFaqs =
    activeCategory === 'All'
      ? faqs
      : faqs.filter(f => f.category === activeCategory);

  const accordionVariants = {
    open: {
      height: "auto",
      opacity: 1,
      transition: { duration: 0.35 }
    },
    closed: {
      height: 0,
      opacity: 0,
      transition: { duration: 0.25 }
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-24 px-6 selection:bg-indigo-500/30">
      <div className="max-w-4xl mx-auto">

        {/* HEADER */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/20 px-4 py-1.5 rounded-full mb-8">
            <SparklesIcon className="h-4 w-4 text-indigo-400" />
            <span className="text-indigo-400 font-semibold text-xs uppercase tracking-wider">
              Support Center
            </span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            Frequently Asked Questions
          </h1>

          <p className="text-gray-400 max-w-2xl mx-auto text-sm md:text-base">
            Everything you need to know about participation, rewards, policies, and platform functionality.
          </p>

          {/* Category Chips */}
          <div className="flex flex-wrap justify-center gap-3 mt-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setOpenFAQ(null);
                  setActiveCategory(cat);
                }}
                className={`px-5 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all border ${
                  activeCategory === cat
                    ? 'bg-indigo-600 border-indigo-600 text-white'
                    : 'bg-[#0a0a0a] border-white/10 text-gray-400 hover:border-white/20 hover:text-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* FAQ LIST */}
        <div className="space-y-4">
          <AnimatePresence>
            {filteredFaqs.map((faq) => {
              const isOpen = openFAQ === faq.id;
              return (
                <motion.div
                  key={faq.id}
                  layout
                  className={`rounded-2xl border transition-all ${
                    isOpen
                      ? 'bg-[#0a0a0a] border-indigo-500/40'
                      : 'bg-[#0a0a0a]/40 border-white/10'
                  }`}
                >
                  <button
                    onClick={() => setOpenFAQ(isOpen ? null : faq.id)}
                    className="w-full p-6 text-left flex justify-between items-center"
                  >
                    <span className={`font-semibold text-base md:text-lg ${
                      isOpen ? 'text-indigo-400' : 'text-gray-300'
                    }`}>
                      {faq.question}
                    </span>

                    <ChevronDownIcon
                      className={`h-5 w-5 transition-transform ${
                        isOpen ? 'rotate-180 text-indigo-400' : 'text-gray-500'
                      }`}
                    />
                  </button>

                  <div className="overflow-hidden px-6">
                    <motion.div
                      variants={accordionVariants}
                      animate={isOpen ? "open" : "closed"}
                    >
                      <div className="pb-6 text-gray-400 text-sm leading-relaxed border-t border-white/5 pt-4">
                        {faq.answer}
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* FOOTER CARDS */}
        <div className="mt-24 grid md:grid-cols-2 gap-8">

          <div className="p-8 bg-[#0a0a0a] border border-white/10 rounded-3xl hover:border-indigo-500/30 transition">
            <TrophyIcon className="h-8 w-8 text-indigo-500 mb-6" />
            <h3 className="text-white font-semibold text-xl mb-3">
              Explore Competitions
            </h3>
            <p className="text-gray-400 text-sm mb-6">
              Discover active challenges and put your expertise to the test.
            </p>
            <Link
              to="/competitions"
              className="inline-flex items-center text-sm font-semibold text-indigo-400 hover:text-indigo-300"
            >
              View Challenges <ArrowRightIcon className="h-4 w-4 ml-2" />
            </Link>
          </div>

          <div className="p-8 bg-gradient-to-br from-indigo-600 to-blue-700 rounded-3xl text-white relative overflow-hidden">
            <ChatBubbleLeftRightIcon className="h-8 w-8 mb-6" />
            <h3 className="font-semibold text-xl mb-3">
              Need Direct Assistance?
            </h3>
            <p className="text-indigo-100/80 text-sm mb-6">
              Our support team typically responds within 2–4 business hours.
            </p>
            <Link
              to="/contact"
              className="px-6 py-3 bg-white text-indigo-600 rounded-lg font-semibold text-sm hover:shadow-lg transition"
            >
              Contact Support
            </Link>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-20 text-center">
          <p className="text-gray-600 text-xs tracking-wider">
            © 2026 GenZEvaluation • All Rights Reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQ;